import type { Ingredient } from '../api/types';

interface FlagHeatmapProps {
  ingredient: Ingredient;
}

interface FlagDef {
  key: keyof Ingredient;
  label: string;
  category: 'allergen' | 'dietary' | 'source';
}

const ALLERGEN_FLAGS: FlagDef[] = [
  { key: 'contains_gluten', label: 'Glu', category: 'allergen' },
  { key: 'contains_dairy', label: 'Dai', category: 'allergen' },
  { key: 'contains_nuts', label: 'Nut', category: 'allergen' },
  { key: 'contains_peanuts', label: 'Pea', category: 'allergen' },
  { key: 'contains_soy', label: 'Soy', category: 'allergen' },
  { key: 'contains_egg', label: 'Egg', category: 'allergen' },
  { key: 'contains_fish', label: 'Fsh', category: 'allergen' },
  { key: 'contains_shellfish', label: 'Shl', category: 'allergen' },
  { key: 'contains_sesame', label: 'Ses', category: 'allergen' },
  { key: 'contains_alcohol', label: 'Alc', category: 'allergen' },
];

const DIETARY_FLAGS: FlagDef[] = [
  { key: 'is_high_sugar', label: 'Sug', category: 'dietary' },
  { key: 'is_high_sodium', label: 'Na', category: 'dietary' },
  { key: 'is_high_saturated_fat', label: 'Fat', category: 'dietary' },
  { key: 'is_trans_fat', label: 'TF', category: 'dietary' },
  { key: 'is_high_cholesterol_source' as keyof Ingredient, label: 'Cho', category: 'dietary' },
];

const SOURCE_FLAGS: FlagDef[] = [
  { key: 'is_animal_derived', label: 'Ani', category: 'source' },
  { key: 'is_plant_derived', label: 'Plt', category: 'source' },
  { key: 'is_synthetic', label: 'Syn', category: 'source' },
  { key: 'source_ambiguous', label: 'Amb', category: 'source' },
];

const ALL_FLAGS = [...ALLERGEN_FLAGS, ...DIETARY_FLAGS, ...SOURCE_FLAGS];

const CATEGORY_COLORS: Record<string, string> = {
  allergen: 'var(--red)',
  dietary: 'var(--amber)',
  source: 'var(--blue)',
};

/**
 * Compact row of tiny colored dots representing active boolean flags.
 * Allergen = red, dietary = amber, source = blue.
 */
export function FlagHeatmap({ ingredient }: FlagHeatmapProps) {
  const activeFlags = ALL_FLAGS.filter((f) => (ingredient[f.key] as number | null) === 1);

  if (activeFlags.length === 0) {
    return <span style={{ color: 'var(--text-tertiary)', fontSize: '0.72rem' }}>{'\u2014'}</span>;
  }

  return (
    <div style={{ display: 'flex', gap: '3px', alignItems: 'center', flexWrap: 'wrap' }}>
      {activeFlags.map((f) => (
        <span
          key={f.key}
          title={f.label}
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: CATEGORY_COLORS[f.category],
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Full flag heatmap grid used in detail views.
 * Shows all flags with labels in a grid layout.
 */
export function FlagHeatmapGrid({ ingredient }: FlagHeatmapProps) {
  return (
    <div class="flag-heatmap">
      {ALL_FLAGS.map((f) => {
        const active = (ingredient[f.key] as number | null) === 1;
        return (
          <div
            key={f.key}
            class={`flag-heatmap-cell ${active ? 'active' : 'inactive'}`}
            title={`${f.label}: ${active ? 'Yes' : 'No'}`}
          >
            {f.label}
          </div>
        );
      })}
    </div>
  );
}
